import { useEffect, useRef, useState } from "react";
import type { Focused } from "react-credit-cards-2";
import { useForm } from "react-hook-form";

import cardValidator from "card-validator";
import { Loader2, Lock } from "lucide-react";

import {
  detectCardBrand,
  formatCardNumber,
  getMaxCvcLength,
} from "js/components/Payments/cardUtils";
import { formatCurrency } from "js/components/Quotas/QuotaConstants";
import { Button } from "js/components/ui/button";
import { Input } from "js/components/ui/input";
import { Label } from "js/components/ui/label";
import { toast } from "utils/toast";

import { setupDeviceSession, tokenizeCard } from "js/utils/openpay";

import CreditCard3D from "./CreditCard3D";

interface CardPaymentFormProps {
  onSubmit: (data: { token_id: string; device_session_id: string }) => void;
  isSubmitting: boolean;
  amount?: number;
  formId?: string;
  hideSubmitButton?: boolean;
}

interface CardFormValues {
  holder_name: string;
  card_number: string;
  expiry: string;
  cvv2: string;
}

function CardPaymentForm({ onSubmit, isSubmitting, amount, formId, hideSubmitButton }: CardPaymentFormProps) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CardFormValues>({
    mode: "onBlur",
    defaultValues: {
      holder_name: "",
      card_number: "",
      expiry: "",
      cvv2: "",
    },
  });

  const [focused, setFocused] = useState<Focused>("");
  const [tokenizeError, setTokenizeError] = useState<string | null>(null);
  const [tokenizing, setTokenizing] = useState(false);
  const deviceSessionRef = useRef<string | null>(null);

  const watchedNumber = watch("card_number");
  const watchedName = watch("holder_name");
  const watchedExpiry = watch("expiry");
  const watchedCvv = watch("cvv2");

  const brand = detectCardBrand(watchedNumber);
  const maxCvc = getMaxCvcLength(brand);

  useEffect(() => {
    try {
      deviceSessionRef.current = setupDeviceSession("payment-form");
    } catch (err) {
      console.warn("Failed to setup device session:", err);
    }
  }, []);

  const validateCardNumber = (value: string): string | true => {
    const digits = value.replace(/\s/g, "");
    if (!digits) return "Ingresa el numero de tarjeta";
    const result = cardValidator.number(digits);
    if (!result.isValid) return "Numero de tarjeta invalido";
    return true;
  };

  const validateHolderName = (value: string): string | true => {
    if (!value.trim()) return "Ingresa el nombre del titular";
    if (value.trim().length < 3) return "Nombre demasiado corto";
    return true;
  };

  const validateExpiry = (value: string): string | true => {
    if (!value) return "Requerido";
    const digits = value.replace(/\D/g, "");
    if (digits.length < 4) return "Ingresa MM/YY";
    const month = digits.slice(0, 2);
    const year = digits.slice(2, 4);
    const m = parseInt(month, 10);
    if (Number.isNaN(m) || m < 1 || m > 12) return "Mes invalido (01-12)";
    const expiryValidation = cardValidator.expirationDate(`${month}/${year}`);
    if (!expiryValidation.isValid) return "Fecha vencida o invalida";
    return true;
  };

  const validateCvv = (value: string): string | true => {
    if (!value) return "Requerido";
    const result = cardValidator.cvv(value, maxCvc);
    if (!result.isValid) return "CVV invalido";
    return true;
  };

  const onFormSubmit = async (data: CardFormValues) => {
    setTokenizing(true);
    setTokenizeError(null);

    let deviceSession = deviceSessionRef.current;
    if (!deviceSession) {
      try {
        deviceSession = setupDeviceSession("payment-form");
        deviceSessionRef.current = deviceSession;
      } catch (err) {
        console.warn("Failed to setup device session:", err);
      }
    }

    if (!deviceSession) {
      toast.error("Error de seguridad: no se pudo inicializar la sesión de pago. Intenta recargar la página.");
      setTokenizing(false);
      return;
    }

    try {
      const expiryDigits = data.expiry.replace(/\D/g, "");
      const expirationMonth = expiryDigits.slice(0, 2);
      const expirationYear = expiryDigits.slice(2, 4);

      const { token_id } = await tokenizeCard({
        holder_name: data.holder_name,
        card_number: data.card_number.replace(/\s/g, ""),
        expiration_month: expirationMonth,
        expiration_year: expirationYear,
        cvv2: data.cvv2,
      });

      onSubmit({
        token_id,
        device_session_id: deviceSession,
      });
    } catch (err) {
      setTokenizeError((err as Error).message);
    } finally {
      setTokenizing(false);
    }
  };

  const isProcessing = tokenizing || isSubmitting;

  return (
    <div className="flex flex-col gap-5">
      <div id="payment-form" style={{ display: "none" }} />

      <div className="flex justify-center py-2">
        <CreditCard3D
          number={watchedNumber}
          name={watchedName}
          expiry={watchedExpiry}
          cvc={watchedCvv}
          focused={focused}
        />
      </div>

      <form
        id={formId}
        onSubmit={handleSubmit(onFormSubmit)}
        className="flex flex-col gap-4"
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="v2_holder_name">Nombre del titular</Label>
          <Input
            id="v2_holder_name"
            placeholder="Como aparece en la tarjeta"
            autoComplete="cc-name"
            disabled={isProcessing}
            data-sentry-ignore
            {...register("holder_name", { validate: validateHolderName })}
            onChange={(e) => {
              const filtered = e.target.value.replace(
                /[^a-zA-Z\u00C0-\u00FF\s]/g,
                "",
              );
              setValue("holder_name", filtered, { shouldValidate: true });
            }}
            onFocus={() => setFocused("name")}
            onBlur={() => setFocused("")}
          />
          {errors.holder_name && (
            <p className="text-xs text-destructive">
              {errors.holder_name.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="v2_card_number">Numero de tarjeta</Label>
            {brand && brand !== "unknown" && (
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                {brand === "visa" ? "VISA" : brand === "mastercard" ? "MC" : brand === "american-express" ? "AMEX" : brand.toUpperCase()}
              </span>
            )}
          </div>
          <Input
            id="v2_card_number"
            placeholder="1234 1234 1234 1234"
            inputMode="numeric"
            autoComplete="cc-number"
            maxLength={19}
            disabled={isProcessing}
            data-sentry-ignore
            {...register("card_number", { validate: validateCardNumber })}
            onChange={(e) => {
              setValue("card_number", formatCardNumber(e.target.value), {
                shouldValidate: true,
              });
            }}
            onFocus={() => setFocused("number")}
            onBlur={() => setFocused("")}
          />
          {errors.card_number && (
            <p className="text-xs text-destructive">
              {errors.card_number.message}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="v2_expiry">Vencimiento</Label>
            <Input
              id="v2_expiry"
              placeholder="MM/YY"
              inputMode="numeric"
              autoComplete="cc-exp"
              maxLength={5}
              disabled={isProcessing}
              data-sentry-ignore
              {...register("expiry", { validate: validateExpiry })}
              onChange={(e) => {
                let raw = e.target.value.replace(/\D/g, "").slice(0, 4);
                if (raw.length >= 3) {
                  raw = `${raw.slice(0, 2)}/${raw.slice(2)}`;
                }
                setValue("expiry", raw, { shouldValidate: true });
              }}
              onFocus={() => setFocused("expiry")}
              onBlur={() => setFocused("")}
            />
            {errors.expiry && (
              <p className="text-xs text-destructive">
                {errors.expiry.message}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="v2_cvv">CVV</Label>
            <Input
              id="v2_cvv"
              placeholder="123"
              inputMode="numeric"
              autoComplete="cc-csc"
              maxLength={maxCvc}
              type="password"
              disabled={isProcessing}
              data-sentry-ignore
              {...register("cvv2", { validate: validateCvv })}
              onChange={(e) => {
                setValue(
                  "cvv2",
                  e.target.value.replace(/\D/g, "").slice(0, maxCvc),
                  { shouldValidate: true },
                );
              }}
              onFocus={() => setFocused("cvc")}
              onBlur={() => setFocused("")}
            />
            {errors.cvv2 && (
              <p className="text-xs text-destructive">{errors.cvv2.message}</p>
            )}
          </div>
        </div>

        {tokenizeError && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {tokenizeError}
          </div>
        )}

        {!hideSubmitButton && (
          <Button type="submit" disabled={isProcessing} className="w-full mt-1">
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Procesando...
              </>
            ) : (
              <>
                <Lock className="mr-2 h-4 w-4" />
                Pagar {amount ? formatCurrency(amount) : "con tarjeta"}
              </>
            )}
          </Button>
        )}
      </form>
    </div>
  );
}

export default CardPaymentForm;
